import { computed, Injectable, signal } from '@angular/core';

import { ZegoQualityGrade } from '../constants';
import { NetworkQualityData, NetworkQualityInfo, RemoteNetworkQualityData } from '../types/call';

@Injectable({
  providedIn: 'root',
})
export class CallNetworkStoreService {
  networkQualityInfo = signal<NetworkQualityInfo>({
    localState: {
      downstreamQuality: ZegoQualityGrade.Unknown,
      upstreamQuality: ZegoQualityGrade.Unknown,
    },
    remoteState: [],
  });

  readonly isShowMyNetworkWarning = computed(() => {
    const info = this.networkQualityInfo();
    return (
      info.localState.upstreamQuality === ZegoQualityGrade.Poor ||
      info.localState.downstreamQuality === ZegoQualityGrade.Poor
    );
  });

  readonly isShowMyNetworkReconnecting = computed(() => {
    const info = this.networkQualityInfo();
    return (
      info.localState.upstreamQuality === ZegoQualityGrade.Die ||
      info.localState.downstreamQuality === ZegoQualityGrade.Die
    );
  });

  readonly isShowRemoteNetworkWarning = computed(() => {
    const info = this.networkQualityInfo();
    return info.remoteState.some(
      (remote) =>
        remote.upstreamQuality === ZegoQualityGrade.Poor ||
        remote.downstreamQuality === ZegoQualityGrade.Poor ||
        remote.upstreamQuality === ZegoQualityGrade.Die ||
        remote.downstreamQuality === ZegoQualityGrade.Die,
    );
  });

  private networkQualityTimer?: ReturnType<typeof setTimeout> | null = null;
  private readonly NETWORK_TIMEOUT = 20000;
  private localDieByDisconnect = false;
  private onNetworkTimeout: (() => void) | null = null;

  setNetworkTimeoutHandler(handler: () => void): void {
    this.onNetworkTimeout = handler;
  }

  handleNetworkQuality(
    userID: string,
    upstreamQuality: number,
    downstreamQuality: number,
    isLoginRoom: boolean,
  ): void {
    if (
      upstreamQuality === ZegoQualityGrade.Unknown &&
      downstreamQuality === ZegoQualityGrade.Unknown &&
      !isLoginRoom
    ) {
      return;
    }

    if (!userID) {
      if (!this.localDieByDisconnect) {
        this.handleNetworkStateChange('local', { upstreamQuality, downstreamQuality });
      }
      return;
    }

    this.handleNetworkStateChange('remote', {
      userId: userID,
      upstreamQuality,
      downstreamQuality,
    });
  }

  forceLocalNetworkDie(): void {
    this.localDieByDisconnect = true;
    this.handleNetworkStateChange('local', {
      upstreamQuality: ZegoQualityGrade.Die,
      downstreamQuality: ZegoQualityGrade.Die,
    });
  }

  onLocalReconnected(): void {
    if (!this.localDieByDisconnect) return;
    this.clearNetworkQualityTimer();
    this.localDieByDisconnect = false;
  }

  resetNetworkQuality(): void {
    this.clearNetworkQualityTimer();
    this.localDieByDisconnect = false;
    this.networkQualityInfo.set({
      localState: {
        upstreamQuality: ZegoQualityGrade.Unknown,
        downstreamQuality: ZegoQualityGrade.Unknown,
      },
      remoteState: [],
    });
  }

  private isWeakNetwork(qualityData: NetworkQualityData): boolean {
    const isWeakUpstream =
      qualityData.upstreamQuality === ZegoQualityGrade.Poor ||
      qualityData.upstreamQuality === ZegoQualityGrade.Die;
    const isWeakDownstream =
      qualityData.downstreamQuality === ZegoQualityGrade.Poor ||
      qualityData.downstreamQuality === ZegoQualityGrade.Die;
    return isWeakUpstream || isWeakDownstream;
  }

  private isPoor(qualityData: NetworkQualityData): boolean {
    return (
      qualityData.upstreamQuality === ZegoQualityGrade.Poor &&
      qualityData.downstreamQuality === ZegoQualityGrade.Poor
    );
  }

  private isDie(qualityData: NetworkQualityData): boolean {
    return (
      qualityData.upstreamQuality === ZegoQualityGrade.Die &&
      qualityData.downstreamQuality === ZegoQualityGrade.Die
    );
  }

  private handleNetworkStateChange(
    type: 'local' | 'remote',
    qualityData: NetworkQualityData | RemoteNetworkQualityData,
  ): void {
    this.networkQualityInfo.update((info) => {
      if (type === 'local') {
        const ld = qualityData as NetworkQualityData;
        if (
          info.localState.upstreamQuality === ld.upstreamQuality &&
          info.localState.downstreamQuality === ld.downstreamQuality
        ) {
          return info;
        }

        const updated = { ...info, localState: ld };
        const oldLocalState = info.localState;

        if (this.isDie(ld)) {
          const wasNotDie = !this.isDie(oldLocalState);
          const hasAnyRemoteDie = updated.remoteState.some((r) => this.isDie(r));
          if (wasNotDie && !hasAnyRemoteDie) {
            this.startNetworkQualityTimer();
          }
        } else if (this.isPoor(ld)) {
          // Keep warning only, no timeout restart.
        } else if (!this.isWeakNetwork(ld)) {
          const allRemoteGood = updated.remoteState.every((r) => !this.isWeakNetwork(r));
          if (allRemoteGood) {
            this.clearNetworkQualityTimer();
          }
        }

        return updated;
      }

      const remoteData = qualityData as RemoteNetworkQualityData;
      const existingIndex = info.remoteState.findIndex((r) => r.userId === remoteData.userId);
      const oldRemoteState = existingIndex >= 0 ? info.remoteState[existingIndex] : null;

      if (
        oldRemoteState &&
        oldRemoteState.upstreamQuality === remoteData.upstreamQuality &&
        oldRemoteState.downstreamQuality === remoteData.downstreamQuality
      ) {
        return info;
      }

      const newRemoteState = [...info.remoteState];
      if (existingIndex >= 0) {
        newRemoteState[existingIndex] = remoteData;
      } else {
        newRemoteState.push(remoteData);
      }
      const updated = { ...info, remoteState: newRemoteState };

      if (this.isDie(remoteData)) {
        const wasNotDie = !oldRemoteState || !this.isDie(oldRemoteState);
        const isLocalDie = this.isDie(updated.localState);
        if (wasNotDie && !isLocalDie) {
          this.startNetworkQualityTimer();
        }
      } else if (this.isPoor(remoteData)) {
        // Keep warning only, no timeout restart.
      } else if (!this.isWeakNetwork(remoteData)) {
        const allRemoteGood = newRemoteState.every((r) => !this.isWeakNetwork(r));
        if (!this.isWeakNetwork(updated.localState) && allRemoteGood) {
          this.clearNetworkQualityTimer();
        }
      }

      return updated;
    });
  }

  private startNetworkQualityTimer(): void {
    this.clearNetworkQualityTimer();
    this.networkQualityTimer = setTimeout(() => {
      this.onNetworkTimeout?.();
    }, this.NETWORK_TIMEOUT);
  }

  private clearNetworkQualityTimer(): void {
    if (this.networkQualityTimer) {
      clearTimeout(this.networkQualityTimer);
      this.networkQualityTimer = null;
    }
  }
}
